import React, { useState } from 'react';
import { Menu, Search, ShoppingCart, MapPin, X, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { locations } from '../mockData';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const Header = () => {
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(locations[0]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const { customer, isAuthenticated } = useCustomerAuth();
  const cartCount = getCartCount();

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setIsStoreModalOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-gradient-to-r from-black via-neutral-950 to-black shadow-lg">
        {/* Top Bar */}
        <div className="border-b border-neutral-800">
          <div className="container mx-auto px-4 py-2 flex items-center justify-between text-sm">
            <button
              onClick={() => setIsStoreModalOpen(true)}
              className="flex items-center gap-2 text-white hover:text-neutral-300 transition-colors"
            >
              <MapPin className="w-4 h-4" />
              <span className="hidden md:inline">
                {selectedStore.name} — {selectedStore.address}
              </span>
              <span className="md:hidden">{selectedStore.name}</span>
            </button>
            <div className="flex items-center gap-4">
              <span className="text-neutral-300 text-xs hidden md:inline">
                Same-Day Delivery Available
              </span>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4 lg:grid lg:grid-cols-[auto_1fr_auto] lg:items-center">
            {/* Logo */}
            <Link to="/" className="flex shrink-0 items-center lg:justify-self-start">
              <img 
                src="/tribal-dash-logo.png" 
                alt="Tribal Dash Cannabis Delivery" 
                className="h-20 md:h-24 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center justify-center gap-6 justify-self-center">
              <Link to="/" className="text-white hover:text-neutral-300 transition-colors font-medium">
                Home
              </Link>
              <Link to="/#shop" className="text-white hover:text-neutral-300 transition-colors font-medium">
                Shop All
              </Link>
              <a href="/#categories" className="text-white hover:text-neutral-300 transition-colors font-medium">
                Categories
              </a>
              <a href="/#about" className="text-white hover:text-neutral-300 transition-colors font-medium">
                About
              </a>
              <a href="/#faq" className="text-white hover:text-neutral-300 transition-colors font-medium">
                FAQ
              </a>
            </nav>

            {/* Right Side Icons */}
            <div className="flex shrink-0 items-center gap-4 lg:justify-self-end">
              <button className="text-white hover:text-neutral-300 transition-colors hidden md:block">
                <Search className="w-5 h-5" />
              </button>
              
              {/* Account Button */}
              {isAuthenticated ? (
                <Link to="/customer/orders" className="text-white hover:text-neutral-300 transition-colors hidden md:flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span className="text-sm">{customer?.firstName}</span>
                </Link>
              ) : (
                <Link to="/customer/login" className="text-white hover:text-neutral-300 transition-colors hidden md:flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span className="text-sm">Login</span>
                </Link>
              )}
              
              <Link to="/checkout" className="relative text-white hover:text-neutral-300 transition-colors">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                className="lg:hidden text-white"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-neutral-950 border-t border-neutral-800">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-3">
              <Link to="/" className="text-white hover:text-neutral-300 transition-colors py-2">
                Home
              </Link>
              <Link to="/#shop" className="text-white hover:text-neutral-300 transition-colors py-2">
                Shop All
              </Link>
              <a href="/#categories" className="text-white hover:text-neutral-300 transition-colors py-2">
                Categories
              </a>
              <a href="/#about" className="text-white hover:text-neutral-300 transition-colors py-2">
                About
              </a>
              <a href="/#faq" className="text-white hover:text-neutral-300 transition-colors py-2">
                FAQ
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Store Selector Modal */}
      <Dialog open={isStoreModalOpen} onOpenChange={setIsStoreModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">{locations[0].name}</DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            <div className="flex gap-4 rounded-lg border p-4">
              <div className="shrink-0 text-neutral-800">
                <img
                  src="/tribal-dash-logo.png"
                  alt="Tribal Dash"
                  className="h-10 w-auto"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm leading-relaxed text-neutral-600">
                  {locations[0].address}
                </p>
                <p className="text-sm text-neutral-600 mt-2">
                  <a
                    href={`tel:${locations[0].phone.replace(/\D/g, '')}`}
                    className="font-medium text-neutral-900 hover:underline"
                  >
                    {locations[0].phone}
                  </a>
                </p>
                <p className="text-sm mt-2">
                  <span className="font-medium text-green-600">
                    {locations[0].status === 'open' ? 'Open' : 'Closed'}
                  </span>
                  <span className="text-neutral-500"> · {locations[0].hours}</span>
                </p>
              </div>
            </div>
            <Button
              onClick={() => handleStoreSelect(locations[0])}
              className="w-full bg-neutral-950 hover:bg-black"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
